import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { trackedFunction } from 'reactiveweb/function';
import { Section } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import Button from 'tio-reimagined/components/button';
import {
  PhArrowRight,
  PhSparkle,
  PhShieldCheck,
  PhCalculator,
  PhTrophy,
  PhListChecks,
} from 'ember-phosphor-icons';
import type CareerPathwaysApi from 'tio-employee/services/career-pathways-api';
import type { PslfJourney, PslfProjection } from 'tio-employee/services/career-pathways-api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { LivePslfData, DataSourceInfo } from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';

// Import our custom PSLF components
import ForgivenessFortuneTeller from 'tio-employee/components/reimagined/pslf/forgiveness-fortune-teller';
import ControlTower from 'tio-employee/components/reimagined/pslf/control-tower';
import ScenarioCard from 'tio-employee/components/reimagined/pslf/scenario-card';
import MilestoneBadge from 'tio-employee/components/reimagined/pslf/milestone-badge';
import PaymentValidationCard from 'tio-employee/components/reimagined/pslf/payment-validation-card';

interface PslfModel {
  clientId: string;
  pslfData: LivePslfData;
  dataSourceInfo: DataSourceInfo;
}

interface PslfDashboardSignature {
  Args: {
    model: PslfModel;
  };
}

class PslfDashboard extends Component<PslfDashboardSignature> {
  @service declare careerPathwaysApi: CareerPathwaysApi;
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get pslfSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    // Only mark as "live" if we have real substantive PSLF data (forms or payments).
    // The page also renders demo journey data (projections, scenarios, achievements)
    // so showing a green "Live Data" badge when we only have an empty profile is misleading.
    if (
      this.liveData.isLiveData &&
      (this.liveData.qualifyingPayments > 0 || this.liveData.forms.length > 0)
    ) {
      return 'live';
    }
    return 'demo';
  }

  get liveData() {
    return this.args.model.pslfData;
  }

  journey = trackedFunction(this, async () => {
    return await this.careerPathwaysApi.fetchPslfJourney();
  });

  get isLoading() {
    return this.journey.isLoading;
  }

  get data(): PslfJourney | null {
    return this.journey.value ?? null;
  }

  /**
   * Merged projection - uses live data for qualifying payments when available,
   * but falls back to demo data for future projections.
   * Returns the demo projection, optionally merged with live qualifying payment data.
   */
  get projection(): PslfProjection {
    // Default projection values (used if no data loaded yet)
    const defaultProjection: PslfProjection = {
      qualifyingPaymentsToDate: 0,
      paymentsRemaining: 120,
      loanBalanceAtForgiveness: 0,
      projectedForgivenessAmount: 0,
      projectedForgivenessDate: '',
      totalPaidToDate: 0,
      totalWillPay: 0,
      forgivenessValue: 0,
      monthlyForgivenessRate: 0,
      yearlyForgivenessRate: 0,
      earnedToDate: 0,
    };

    const baseProjection = this.data?.projection ?? defaultProjection;

    // If we have live data, merge it with demo projection structure
    if (this.liveData.isLiveData) {
      const qualifyingPayments = this.liveData.qualifyingPayments;
      const remaining = 120 - qualifyingPayments;

      return {
        ...baseProjection,
        qualifyingPaymentsToDate: qualifyingPayments,
        paymentsRemaining: remaining,
        // Use live estimated forgiveness if available
        projectedForgivenessAmount:
          this.liveData.estimatedForgiveness || baseProjection.projectedForgivenessAmount,
        projectedForgivenessDate:
          this.liveData.forgivenessDate || baseProjection.projectedForgivenessDate,
      };
    }

    return baseProjection;
  }

  get topAchievements() {
    // Show most recent 4 unlocked achievements
    if (!this.data?.achievements) return [];
    return this.data.achievements
      .filter((a) => a.achievedAt !== null)
      .sort((a, b) => new Date(b.achievedAt!).getTime() - new Date(a.achievedAt!).getTime())
      .slice(0, 4);
  }

  get recentPayments() {
    // Show most recent 3 payments from demo data
    // Note: Live forms are for ECF certification, not individual payment validation
    // Future: could integrate with loan servicer API for actual payment history
    if (!this.data?.recentPayments) return [];
    return this.data.recentPayments.slice(0, 3);
  }

  get recentForms() {
    // Use live form data if available for ECF history
    if (this.liveData.isLiveData && this.liveData.forms.length > 0) {
      return this.liveData.forms.slice(0, 3);
    }
    return [];
  }

  get topScenarios() {
    // Show 2 most relevant scenarios
    if (!this.data?.scenarios) return [];
    return this.data.scenarios.slice(0, 2);
  }

  handleScenarioExplore = (_scenarioType: string) => {
    // Navigate to scenario detail page when implemented
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="data-audit min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-slate-900 dark:to-gray-950"
    >
      {{#if this.isLoading}}
        {{! Loading state }}
        <div class="flex items-center justify-center h-96">
          <div class="text-center">
            <div
              class="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"
            ></div>
            <Text class="text-gray-600">Loading your PSLF journey...</Text>
          </div>
        </div>
      {{else if this.data}}
        <DataIndicator @source={{this.pslfSource}} @showBadge={{true}} @dataSourceId="pslf-journey">
          {{! Hero Section - Fortune Teller }}
          <div class="relative">
            {{! Decorative background }}
            <div class="absolute inset-0 overflow-hidden">
              <div
                class="absolute top-20 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl"
              ></div>
              <div
                class="absolute top-40 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"
              ></div>
            </div>

            <div class="relative z-10 max-w-7xl mx-auto px-6 py-12">
              {{! Welcome header }}
              <div class="mb-8">
                <div class="flex items-center gap-2 mb-2">
                  <PhSparkle @size={{24}} class="text-violet-600 dark:text-violet-400" />
                  <Text
                    class="text-violet-600 dark:text-violet-400 font-semibold uppercase tracking-wide text-sm"
                  >
                    PSLF Reimagined
                  </Text>
                </div>
                <Heading @level={{1}} class="text-gray-900 dark:text-white text-4xl font-bold">
                  Your Path to Forgiveness
                </Heading>
                <Text class="text-gray-600 mt-2 text-lg">
                  {{this.projection.qualifyingPaymentsToDate}}
                  qualifying payments and counting
                </Text>
              </div>

              {{! Main fortune teller - use merged projection with live data }}
              <ForgivenessFortuneTeller @projection={{this.projection}} class="mb-8" />

              {{! Quick action buttons }}
              <div class="flex flex-wrap gap-4">
                <LinkTo
                  @route="authenticated.reimagined.pslf.payments"
                  class="inline-flex items-center gap-2 px-4 py-2 border border-violet-500 text-violet-600 dark:text-violet-400 rounded-lg hover:bg-violet-500/10 transition-colors"
                >
                  <PhListChecks @size={{20}} />
                  <span>View All Payments</span>
                </LinkTo>
                <LinkTo
                  @route="authenticated.reimagined.pslf.scenarios"
                  class="inline-flex items-center gap-2 px-4 py-2 border border-violet-500 text-violet-600 dark:text-violet-400 rounded-lg hover:bg-violet-500/10 transition-colors"
                >
                  <PhCalculator @size={{20}} />
                  <span>What-If Calculator</span>
                </LinkTo>
                <LinkTo
                  @route="authenticated.reimagined.pslf.milestones"
                  class="inline-flex items-center gap-2 px-4 py-2 border border-violet-500 text-violet-600 dark:text-violet-400 rounded-lg hover:bg-violet-500/10 transition-colors"
                >
                  <PhTrophy @size={{20}} />
                  <span>Achievements</span>
                </LinkTo>
              </div>
            </div>
          </div>

          {{! Main content grid }}
          <div class="max-w-7xl mx-auto px-6 pb-12">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {{! Left column - Control Tower + Payments }}
              <div class="lg:col-span-2 space-y-8">
                {{! Control Tower }}
                <Section>
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-2">
                      <PhShieldCheck @size={{20}} class="text-violet-600 dark:text-violet-400" />
                      <Heading
                        @level={{2}}
                        class="text-gray-900 dark:text-white text-xl font-semibold"
                      >
                        Autopilot Status
                      </Heading>
                    </div>
                  </div>
                  <ControlTower
                    @automationStatus={{this.data.automationStatus}}
                    @protectionStatus={{this.data.protectionStatus}}
                  />
                </Section>

                {{! Recent Payments }}
                <Section>
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-2">
                      <PhListChecks @size={{20}} class="text-violet-600 dark:text-violet-400" />
                      <Heading
                        @level={{2}}
                        class="text-gray-900 dark:text-white text-xl font-semibold"
                      >
                        Recent Payments
                      </Heading>
                    </div>
                    <LinkTo
                      @route="authenticated.reimagined.pslf.payments"
                      class="inline-flex items-center gap-1 text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 text-sm"
                    >
                      <span>View All</span>
                      <PhArrowRight @size={{16}} />
                    </LinkTo>
                  </div>
                  <div class="space-y-4">
                    {{#each this.recentPayments as |payment|}}
                      <PaymentValidationCard @payment={{payment}} @compact={{true}} />
                    {{/each}}
                  </div>
                </Section>
              </div>

              {{! Right column - Scenarios + Achievements }}
              <div class="space-y-8">
                {{! What-If Scenarios }}
                <Section>
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-2">
                      <PhCalculator @size={{20}} class="text-violet-600 dark:text-violet-400" />
                      <Heading
                        @level={{2}}
                        class="text-gray-900 dark:text-white text-lg font-semibold"
                      >
                        What-If Scenarios
                      </Heading>
                    </div>
                    <LinkTo
                      @route="authenticated.reimagined.pslf.scenarios"
                      class="inline-flex items-center gap-1 text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 text-sm"
                    >
                      <span>All</span>
                      <PhArrowRight @size={{16}} />
                    </LinkTo>
                  </div>
                  <div class="space-y-4">
                    {{#each this.topScenarios as |scenario|}}
                      <ScenarioCard
                        @scenario={{scenario}}
                        @onExplore={{this.handleScenarioExplore}}
                      />
                    {{/each}}
                  </div>
                </Section>

                {{! Achievements }}
                <Section>
                  <div class="flex items-center justify-between mb-4">
                    <div class="flex items-center gap-2">
                      <PhTrophy @size={{20}} class="text-violet-600 dark:text-violet-400" />
                      <Heading
                        @level={{2}}
                        class="text-gray-900 dark:text-white text-lg font-semibold"
                      >
                        Achievements
                      </Heading>
                    </div>
                    <LinkTo
                      @route="authenticated.reimagined.pslf.milestones"
                      class="inline-flex items-center gap-1 text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 text-sm"
                    >
                      <span>All</span>
                      <PhArrowRight @size={{16}} />
                    </LinkTo>
                  </div>
                  <div class="flex flex-wrap gap-3 justify-center">
                    {{#each this.topAchievements as |achievement|}}
                      <MilestoneBadge @achievement={{achievement}} @compact={{true}} />
                    {{/each}}
                  </div>
                  {{#unless this.topAchievements.length}}
                    <Text class="text-gray-500 text-center text-sm">
                      Make your first qualifying payment to start earning achievements!
                    </Text>
                  {{/unless}}
                </Section>
              </div>
            </div>
          </div>

          {{! Bottom CTA }}
          <div
            class="bg-gradient-to-r from-violet-100 via-purple-100 to-violet-100 dark:from-violet-900/50 dark:via-purple-900/50 dark:to-violet-900/50 py-12"
          >
            <div class="max-w-4xl mx-auto px-6 text-center">
              <Heading @level={{2}} class="text-gray-900 dark:text-white text-2xl font-bold mb-4">
                Questions about your PSLF journey?
              </Heading>
              <Text class="text-gray-600 mb-6">
                Our AI assistant can help you understand your eligibility, optimize your path, and
                answer any questions about Public Service Loan Forgiveness.
              </Text>
              <Button @intent="primary">
                <PhSparkle @size={{20}} />
                <span>Ask AI Assistant</span>
              </Button>
            </div>
          </div>
        </DataIndicator>
      {{else}}
        {{! Error state }}
        <div class="flex items-center justify-center h-96">
          <div class="text-center">
            <Text class="text-gray-600">Unable to load your PSLF data. Please try again.</Text>
            <Button @intent="primary" class="mt-4">
              <span>Retry</span>
            </Button>
          </div>
        </div>
      {{/if}}
    </div>
  </template>
}

export default PslfDashboard;
